import { FastField, Field, Form } from 'formik'
import React, { Fragment } from 'react'
import { Link, useParams } from 'react-router-dom'
import { NewCustomerTDO } from '../../types/customer'
import Card from '../Card'
import FormikDragZone from '../FormikDragZone'
import FormikInput from '../FormikInput'
import FormikTextarea from '../FormikTextarea'
import GoogleSearch from '../GoogleSearch'
import LocationValue from '../LocationValue'
import { Place } from '../../types/utils'
import DeleteButton from '../DeleteButton'
import { useMutation } from 'react-query'
import customerService from '../../services/customerService'
import { queryClient } from '../../utils/react-query'
import toast from 'react-hot-toast'
import Spinner from '../Spinner'
import { useUser } from '../../hooks/useUser'

type CustomerFormProps = {
  isSubmitting: boolean
  isDisabled: boolean
  currentAvatar?: string
  currentFallbackImage?: string
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  values: NewCustomerTDO | Partial<NewCustomerTDO>
  customerId?: string
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  isSubmitting,
  setFieldValue,
  values,
  isDisabled,
  currentAvatar,
  currentFallbackImage,
  customerId: defaultCustomerId,
}) => {
  const { customerId = defaultCustomerId } = useParams<{ customerId: string }>()
  const user = useUser()
  const places = values.places || []

  const handleDelete = useMutation(customerService.deleteCustomer, {
    onSuccess: async () => {
      queryClient.refetchQueries('customers')
      toast.success('Customer Deleted.')
    },
    onError: (error) => {
      console.log('🚀 ~ file: CustomerForm.tsx ~ line 44 ~ error', error)
      toast.error('Something went wrong, if the error persist please contact support.')
    },
  })

  return (
    <Form className="mb-5">
      {!customerId && (
        <div className="mb-10 flex items-center justify-between">
          <h1 className="page-title">New Customer</h1>
          <button type="submit" className="btn btn-primary" disabled={isDisabled}>
            Create Customer
          </button>
        </div>
      )}

      <Card.Container>
        <Card.Heading>
          <Card.HeadingLeft>
            <h3 className="card-title">Customer Details</h3>
          </Card.HeadingLeft>
          <Card.HeadingRight>
            {customerId ? (
              <DeleteButton
                type="button"
                className="btn btn-delete"
                modalTitle="Delete Customer"
                modalText={`You are about to delete ${
                  values?.name || 'this customer'
                }. This action can be undone at any stage. Are you sure you want to delete this customer?`}
                disabled={handleDelete.isLoading}
                onClick={async () => {
                  if (customerId) {
                    await handleDelete.mutateAsync({
                      id: customerId,
                    })
                  }
                }}
              >
                {handleDelete.isLoading ? (
                  <div className="mr-3">
                    <Spinner className="trailing-icon h-4 w-4" />
                  </div>
                ) : null}
                Delete Customer
              </DeleteButton>
            ) : null}
          </Card.HeadingRight>
        </Card.Heading>
        <Card.Field col>
          <fieldset disabled={isSubmitting} className="mb-6">
            <FastField type="text" component={FormikInput} label="Customer Name*" placeholder="Acme Ltd." name="name" />
          </fieldset>

          <fieldset disabled={isSubmitting} className="mb-6">
            <FastField
              type="text"
              component={FormikInput}
              label="Primary Contact Email*"
              placeholder="you@example.com"
              name="adminEmail"
            />
          </fieldset>

          <fieldset disabled={isSubmitting} className="mb-6">
            <FastField
              component={FormikTextarea}
              label="Fallback Description*"
              placeholder="Fallback Description"
              name="fallbackDescription"
            />
            <p className="mt-2 text-sm font-normal leading-5 text-gray-500">
              If no location description is given, this fallback will be used.
            </p>
          </fieldset>

          <fieldset disabled={isSubmitting} className="mb-6 grid gap-5 lg:grid-cols-2">
            <FastField
              type="text"
              component={FormikInput}
              label="Fallback Action Button Text*"
              placeholder="Book Today"
              name="fallbackActionText"
            />
            <FastField
              type="url"
              component={FormikInput}
              label="Fallback Button URL*"
              placeholder="www.example.com"
              name="fallbackActionUrl"
            />
          </fieldset>

          <fieldset disabled={isSubmitting} className="mb-6 grid items-start gap-5 lg:grid-cols-2">
            <Field
              component={FormikDragZone}
              label="Fallback Image*"
              name="fallbackImage"
              placeholderImage={currentFallbackImage}
            />
            <Field component={FormikDragZone} label="Customer Avatar*" name="avatar" placeholderImage={currentAvatar} />
          </fieldset>
        </Card.Field>
      </Card.Container>
      {user.data?.type === 'BUZZL' ? (
        <Card.Container>
          <Card.Heading>
            <Card.HeadingLeft>
              <h3 className="card-title">Customer Locations</h3>
            </Card.HeadingLeft>
          </Card.Heading>

          <Card.Field col>
            <fieldset disabled={isSubmitting} className="mb-6">
              <FastField
                type="number"
                component={FormikInput}
                label="Assigned Licences*"
                placeholder="0"
                name="totalLicenses"
              />
            </fieldset>

            {!customerId && (
              <fieldset disabled={isSubmitting} className="mb-6">
                <Field
                  component={GoogleSearch}
                  menuPlacement="top"
                  label="Location Name"
                  handleSelect={(selected: Place) => {
                    const exist = places.some(({ googlePlaceId }) => googlePlaceId === selected.googlePlaceId)

                    if (!exist) {
                      setFieldValue('places', [...places, selected])
                    }
                  }}
                />
              </fieldset>
            )}

            {!customerId &&
              places.length > 0 &&
              places.map(({ googlePlaceId, description }) => (
                <Fragment key={googlePlaceId}>
                  <LocationValue
                    label="Location Name"
                    action={() => {
                      const filter = places.filter((places) => places.googlePlaceId !== googlePlaceId)
                      setFieldValue('places', filter)
                    }}
                    value={description}
                  />
                </Fragment>
              ))}
          </Card.Field>
        </Card.Container>
      ) : null}

      <div className="flex justify-end gap-5">
        <Link to="/customers" className="btn btn-white">
          Cancel
        </Link>
        <button className="btn btn-primary" type="submit" disabled={isDisabled}>
          {customerId ? 'Update' : 'Create Customer'}
        </button>
      </div>
    </Form>
  )
}

export default CustomerForm
